import React, { useState, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Modal,
} from "@material-ui/core";
import { CircularProgress } from "react-md";
import Button from "react-md/lib/Buttons/Button";
import moment from "moment";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import NavigoAPIServices from "../services/navigoAPIServices";
import AuthenticationService from "../../../../../service/auth/AuthenticationService";
import EventService from "../../../../../service/event/EventService";
import "../styles/ClaimStatusCell.scss";

const navigoAPIServices = new NavigoAPIServices();
const authenticationService = new AuthenticationService();

// A status-history comment comes from a rich-text editor, so an "empty" remark is
// often HTML like "<p></p>" / "<p><br></p>" rather than "". Strip tags/entities to
// decide whether there's any real text to show.
const remarkHasText = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim().length > 0;

// Reject reasons are a small, static list shared by every row. Cache them at the
// module level (with a shared in-flight promise) so the dashboard fetches them
// ONCE instead of once per ClaimStatusCell (N rows was firing N identical calls).
let rejectReasonsCache = null;
let rejectReasonsInFlight = null;
const getRejectReasonsCached = () => {
  if (rejectReasonsCache) return Promise.resolve(rejectReasonsCache);
  if (rejectReasonsInFlight) return rejectReasonsInFlight;
  rejectReasonsInFlight = new Promise((resolve) => {
    navigoAPIServices.rejectReason(
      (res) => {
        rejectReasonsCache = Array.isArray(res) ? res : [];
        rejectReasonsInFlight = null;
        resolve(rejectReasonsCache);
      },
      () => {
        rejectReasonsInFlight = null; // allow a retry later; don't cache failure
        resolve([]);
      },
    );
  });
  return rejectReasonsInFlight;
};

const getStatusName = (i) => {
  if (i == 1) {
    return "Recieved";
  } else if (i == 2) {
    return "Approved";
  } else if (i == 3) {
    return "Rejected";
  } else if (i == 4) {
    return "Processed";
  } else if (i == 5) {
    return "Appealed";
  } else if (i == 6) {
    return "On Hold";
  } else if (i == 7) {
    return "On Provisional Approved";
  } else if (i == 9) {
    return "Draft";
  } else {
    return "NA";
  }
};

/**
 * Self-contained claim status dropdown + status-transition modals.
 * Extracted verbatim from claims-dashboard.js so it can be shared between the
 * legacy dashboard and the new AI dashboard.
 *
 * Props:
 *   claim     - the claim row. Must expose: id, walletId, currentStatus,
 *               ticketEvents, patientId, claimedFor, planId, claimAmount,
 *               reimbursementTypeId, eligibleClaimAmount, claimId.
 *   onUpdated - callback fired after any successful status update (list refresh).
 */
function ClaimStatusCell({ claim, onUpdated }) {
  const id = claim?.id;
  const walletId = claim?.walletId;
  const selectedClaimRow = claim;

  // ----- Modal-open flags (component owned) -----
  const [rejectModal, setRejectModal] = useState(false);
  const [approvedModal, setApproveModal] = useState(false);
  const [processedModal, setProcessedModal] = useState(false);
  const [onHoldModal, setOnHoldModal] = useState(false);
  const [onResubmittedModal, setOnResubmittedModal] = useState(false);
  const [onProvisionalModal, setOnProvisionalModal] = useState(false);

  // ----- Form fields -----
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [approveClaim, setApproveClaim] = useState();
  const [approveRemarks, setApproveRemarks] = useState("");
  const [processClaim, setProcessClaim] = useState();
  const [processRemarks, setProcessRemarks] = useState("");
  const [onHoldRemarks, setOnHoldRemarks] = useState("");
  const [
    onHoldUploadDocumentsAgainCheckbox,
    setOnHoldUploadDocumentsAgainCheckbox,
  ] = useState(false);
  const [onProvisonalApprovedRemark, setOnProvisonalApprovedRemark] =
    useState("");
  const [onResubmittedRemark, setOnResubmittedRemark] = useState("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // ----- Flow / UX flags -----
  const [isContinueClicked, setIsContinueClicked] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [showHoldConfirmation, setShowHoldConfirmation] = useState(false);
  const [clickedClaimTicketEvents, setClickedClaimTicketEvents] = useState([]);
  const [reasonList, setReasonList] = useState([]);

  // ----- Approve-amount state -----
  const [currAvailableClaimAmt, setCurrAvailableClaimAmt] = useState(null);
  const [fetchingAvaClaimAmt, setFetchingAvaClaimAmt] = useState(false);

  // ----- Load reject reasons (shared/cached — one fetch for the whole page) -----
  useEffect(() => {
    let mounted = true;
    getRejectReasonsCached().then((list) => {
      if (mounted) setReasonList(list);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const isFormFilled =
    (reason && remarks != null && remarks != "") ||
    approveClaim ||
    processClaim ||
    (onHoldRemarks != null && onHoldRemarks != "") ||
    (onProvisonalApprovedRemark != null && onProvisonalApprovedRemark != "") ||
    (onResubmittedRemark != null && onResubmittedRemark != "");

  const resetFormFields = () => {
    setProcessClaim("");
    setProcessRemarks("");
    setReason("");
    setRemarks("");
    setApproveClaim("");
    setApproveRemarks("");
    setOnHoldRemarks("");
    setOnProvisonalApprovedRemark("");
    setOnResubmittedRemark("");
    setOnHoldUploadDocumentsAgainCheckbox(false);
    setShowHoldConfirmation(false);
    setIsContinueClicked(false);
  };

  const handleCloseModal = () => {
    setRejectModal(false);
    setApproveModal(false);
    setProcessedModal(false);
    setOnHoldModal(false);
    setOnProvisionalModal(false);
    setOnResubmittedModal(false);
    resetFormFields();
    setEditorState(EditorState.createEmpty());
  };

  const handleEditorChange = (newState, setter) => {
    setEditorState(newState);
    const rawContentState = convertToRaw(newState.getCurrentContent());
    const htmlContent = draftToHtml(rawContentState);
    setter(htmlContent);
  };

  const handleContinue = () => {
    if (reason) {
      setIsContinueClicked(true);
    } else if (approveClaim) {
      setIsContinueClicked(true);
    } else if (processClaim) {
      setIsContinueClicked(true);
    }
  };

  const fetchCurrAvailableClaimAmt = (ele) => {
    setFetchingAvaClaimAmt(true);
    const body = {
      patientId: ele.patientId,
      claimedFor: ele.claimedFor,
      planId: ele.planId,
      claimAmount: ele.claimAmount,
      claimTypeId: ele.reimbursementTypeId,
      blockedAmount: ele.eligibleClaimAmount,
      ignorePlanExpiry: true,
      claimId: ele.claimId ?? "",
    };

    navigoAPIServices.fetchCurrAvailableClaimAmt(
      body,
      (res) => {
        setCurrAvailableClaimAmt(res ? res?.eligibleAmount : null);
        setFetchingAvaClaimAmt(false);
      },
      (err) => {
        setCurrAvailableClaimAmt(null);
        setFetchingAvaClaimAmt(false);
        EventService.emit("showError", {
          message: `Error: ${err?.message}`,
        });
      },
    );
  };

  // ------------------------------------------------------------------
  // Modal-trigger handlers
  // ------------------------------------------------------------------
  const handleApproveModal = (ele) => {
    fetchCurrAvailableClaimAmt(ele);
    setApproveModal(true);
    resetFormFields();
  };

  const handleRejectClick = () => {
    setRejectModal(true);
    resetFormFields();
  };

  const handleProcessedClick = () => {
    setProcessedModal(true);
    resetFormFields();
  };

  const handleAppealedClick = () => {
    appealedClick(id, walletId);
    resetFormFields();
  };

  const handleOnHoldClick = (ticketEvents) => {
    setShowHoldConfirmation(false);
    setClickedClaimTicketEvents(ticketEvents ?? []);
    setOnHoldModal(true);
    resetFormFields();
  };

  const handleProvisionalApprovedClick = (ticketEvents) => {
    setOnProvisonalApprovedRemark("");
    setClickedClaimTicketEvents(ticketEvents ?? []);
    setOnProvisionalModal(true);
    resetFormFields();
  };

  const handleOnResubmittedClick = (ticketEvents) => {
    setClickedClaimTicketEvents(ticketEvents ?? []);
    setOnResubmittedRemark("");
    setOnResubmittedModal(true);
    resetFormFields();
  };

  // ------------------------------------------------------------------
  // Submit handlers (exact updateClaim payloads preserved)
  // ------------------------------------------------------------------
  const deductMoney = (id, walletId, approveClaim, approveRemarks) => {
    setIsClicked(true);
    const floatClaim = parseFloat(approveClaim);
    const approvedAmount = isNaN(floatClaim) || floatClaim < 0 ? 0 : floatClaim;

    const user = authenticationService.getUserProfile();
    const fName = user.employeeProfile.firstName;
    const lName = user.employeeProfile.lastName;

    const payload = {
      walletId: walletId,
      statusUpdate: {
        id: 2,
        approvedClaimAmount: approvedAmount,
        employeeName: `${fName} ${lName ? lName : ""}`,
        comment: approveRemarks,
      },
    };

    navigoAPIServices.updateClaim(
      id,
      payload,
      (response) => {
        console.log("Claim updated successfully:", response);
        setIsClicked(false);
        setApproveModal(false);
        onUpdated && onUpdated();
      },
      (error) => {
        console.error("Error updating claim:", error);
        setIsClicked(false);
        setIsContinueClicked(false);
        EventService.emit("showError", {
          message:
            error?.message ??
            "Something went wrong while updating the claim status.",
        });
      },
    );
  };

  const processedClaim = (id, walletId, transactionId, processRemarks) => {
    let user = authenticationService.getUserProfile();
    let fName = user.employeeProfile.firstName;
    let lName = user.employeeProfile.lastName;
    const payload = {
      walletId: walletId,
      statusUpdate: {
        id: 4,
        transactionId: transactionId,
        employeeName: `${fName} ${lName ? lName : ""}`,
        comment: processRemarks,
      },
    };
    navigoAPIServices.updateClaim(
      id,
      payload,
      (response) => {
        console.log("Claim updated successfully:", response);
        setProcessedModal(false);
        onUpdated && onUpdated();
      },
      (error) => {
        console.error("Error updating claim:", error);
      },
    );
  };

  const onHoldClaim = (
    id,
    walletId,
    onHoldRemarks,
    onHoldUploadDocumentsAgainCheckbox,
  ) => {
    setIsClicked(true);
    let user = authenticationService.getUserProfile();
    let fName = user.employeeProfile.firstName;
    let lName = user.employeeProfile.lastName;
    const payload = {
      walletId: walletId,
      statusUpdate: {
        id: 6,
        employeeName: `${fName} ${lName ? lName : ""}`,
        comment: onHoldRemarks,
        uploadDocumentsAgain: onHoldUploadDocumentsAgainCheckbox ?? false,
      },
    };
    navigoAPIServices.updateClaim(
      id,
      payload,
      (response) => {
        setIsClicked(false);
        console.log("Claim updated successfully:", response);
        setOnHoldModal(false);
        setShowHoldConfirmation(false);
        resetFormFields();
        onUpdated && onUpdated();
        EventService.emit("showError", {
          message: "Claim status updated successfully",
        });
      },
      (error) => {
        setIsClicked(false);
        console.error("Error updating claim:", error);
        resetFormFields();
        EventService.emit("showError", {
          message: error?.message ?? "Error while updating claim status",
        });
      },
    );
  };

  const handleProvisionedApprovedUpdate = (
    id,
    walletId,
    provisonalApprovedRemark,
  ) => {
    setIsClicked(true);
    let user = authenticationService.getUserProfile();
    let fName = user.employeeProfile.firstName;
    let lName = user.employeeProfile.lastName;
    const payload = {
      walletId: walletId,
      statusUpdate: {
        id: 7,
        employeeName: `${fName} ${lName ? lName : ""}`,
        comment: provisonalApprovedRemark,
      },
    };
    navigoAPIServices.updateClaim(
      id,
      payload,
      (response) => {
        setIsClicked(false);
        console.log("Claim updated successfully:", response);
        setOnProvisionalModal(false);
        setOnProvisonalApprovedRemark("");
        resetFormFields();
        onUpdated && onUpdated();
        EventService.emit("showError", {
          message: "Claim status updated successfully",
        });
      },
      (error) => {
        setIsClicked(false);
        console.error("Error updating claim:", error);
        resetFormFields();
        EventService.emit("showError", {
          message: error?.message ?? "Error while updating claim status",
        });
      },
    );
  };

  const handleResubmittedUpdate = (id, walletId, remark) => {
    setIsClicked(true);
    let user = authenticationService.getUserProfile();
    let fName = user.employeeProfile.firstName;
    let lName = user.employeeProfile.lastName;
    const payload = {
      walletId: walletId,
      statusUpdate: {
        id: 8,
        employeeName: `${fName} ${lName ? lName : ""}`,
        comment: remark,
      },
    };
    navigoAPIServices.updateClaim(
      id,
      payload,
      (response) => {
        setIsClicked(false);
        console.log("Claim updated successfully:", response);
        setOnResubmittedModal(false);
        resetFormFields();
        onUpdated && onUpdated();
        EventService.emit("showError", {
          message: "Claim status updated successfully",
        });
      },
      (error) => {
        setIsClicked(false);
        console.error("Error updating claim:", error);
        resetFormFields();
        EventService.emit("showError", {
          message: error?.message ?? "Error while updating claim status",
        });
      },
    );
  };

  const rejectClaim = (id, walletId, reason, remarks) => {
    let user = authenticationService.getUserProfile();
    let fName = user.employeeProfile.firstName;
    let lName = user.employeeProfile.lastName;
    const payload = {
      walletId: walletId,
      statusUpdate: {
        id: 3,
        comment: remarks,
        employeeName: `${fName} ${lName ? lName : ""}`,
        rejectReason: reason,
      },
    };
    navigoAPIServices.updateClaim(
      id,
      payload,
      (response) => {
        console.log("Claim updated successfully:", response);
        setRejectModal(false);
        onUpdated && onUpdated();
      },
      (error) => {
        console.error("Error updating claim:", error);
      },
    );
  };

  const appealedClick = (id, walletId) => {
    let user = authenticationService.getUserProfile();
    let fName = user.employeeProfile.firstName;
    let lName = user.employeeProfile.lastName;
    const payload = {
      walletId: walletId,
      statusUpdate: {
        id: 5,
        employeeName: `${fName} ${lName ? lName : ""}`,
      },
    };
    navigoAPIServices.updateClaim(
      id,
      payload,
      (res) => {
        console.log("updated successfully");
        onUpdated && onUpdated();
      },
      (err) => {
        console.log("not able to update");
      },
    );
  };

  const closeButtonStyle = {
    position: "absolute",
    top: "10px",
    right: "10px",
    background: "none",
    border: "none",
    cursor: "pointer",
    width: "53px",
    height: "53px",
    borderRadius: "70%",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#333",
    fontWeight: "bold",
    fontSize: "17px",
  };

  const menuItemStyle = {
    borderBottomLeftRadius: "8px",
    borderBottomRightRadius: "8px",
    fontWeight: "600",
    paddingLeft: "5px",
    fontFamily: "Montserrat",
  };

  return (
    <div className="claim-status-cell-root">
      {claim.currentStatus !== 9 ? (
        <Select
          labelId="navigo-appoint-status-select"
          id="navigo-appoint-status-select-id"
          value={claim.currentStatus}
          onChange={() => {}}
          label="Status"
          className={`navigo-dashboard-select-css navigo-status-selector ${
            claim.currentStatus === 2 ||
            claim.currentStatus === 6 ||
            claim.currentStatus == 7
              ? "pending-class"
              : `${
                  claim.currentStatus === 3
                    ? "invalid-class"
                    : `${
                        claim.currentStatus === 4
                          ? "closed-class"
                          : claim.currentStatus === 1
                            ? "progress-class"
                            : "progress-class"
                      }`
                }`
          }`}
        >
          <MenuItem
            value={1}
            style={{
              borderBottom: "1px solid #d8d8d8",
              fontWeight: "600",
              paddingLeft: "5px",
              fontFamily: "Montserrat",
            }}
            disabled={
              claim.currentStatus == 4 ||
              claim.currentStatus == 2 ||
              claim.currentStatus == 5 ||
              claim.currentStatus == 3 ||
              claim.currentStatus == 1 ||
              claim.currentStatus == 6 ||
              claim.currentStatus == 7 ||
              claim.currentStatus == 8
            }
          >
            Received
          </MenuItem>

          <MenuItem
            value={2}
            style={menuItemStyle}
            onClick={() => handleApproveModal(claim)}
            disabled={
              claim.currentStatus == 4 ||
              claim.currentStatus == 3 ||
              claim.currentStatus == 2
            }
          >
            Approved
          </MenuItem>

          <MenuItem
            value={3}
            style={menuItemStyle}
            onClick={() => handleRejectClick()}
            disabled={
              claim.currentStatus == 4 ||
              claim.currentStatus == 2 ||
              claim.currentStatus == 3
            }
          >
            Rejected
          </MenuItem>

          <MenuItem
            value={4}
            style={menuItemStyle}
            disabled={
              claim.currentStatus == 4 ||
              claim.currentStatus == 5 ||
              claim.currentStatus == 3 ||
              claim.currentStatus == 1 ||
              claim.currentStatus == 6 ||
              claim.currentStatus == 7 ||
              claim.currentStatus == 8
            }
            onClick={() => {
              handleProcessedClick();
            }}
          >
            Processed
          </MenuItem>
          <MenuItem
            value={5}
            style={menuItemStyle}
            onClick={() => {
              handleAppealedClick();
            }}
            disabled={
              claim.currentStatus == 4 ||
              claim.currentStatus == 2 ||
              claim.currentStatus == 1 ||
              claim.currentStatus == 5 ||
              claim.currentStatus == 6 ||
              claim.currentStatus == 7 ||
              claim.currentStatus == 8
            }
          >
            Appealed
          </MenuItem>
          <MenuItem
            value={6}
            style={menuItemStyle}
            onClick={() => {
              handleOnHoldClick(claim.ticketEvents);
            }}
            disabled={
              !(
                claim.currentStatus == 1 ||
                claim.currentStatus == 5 ||
                claim.currentStatus == 6 ||
                claim.currentStatus == 7 ||
                claim.currentStatus == 8
              )
            }
          >
            On Hold
          </MenuItem>
          <MenuItem
            value={7}
            style={menuItemStyle}
            onClick={() => {
              handleProvisionalApprovedClick(claim.ticketEvents);
            }}
            disabled={
              claim.currentStatus == 2 ||
              claim.currentStatus == 3 ||
              claim.currentStatus == 4 ||
              claim.currentStatus == 7
            }
          >
            Prv. Approved
          </MenuItem>
          <MenuItem
            value={8}
            style={menuItemStyle}
            onClick={() => {
              handleOnResubmittedClick(claim.ticketEvents);
            }}
            disabled={!(claim.currentStatus == 6)}
          >
            Resubmitted
          </MenuItem>
        </Select>
      ) : (
        <div>
          <span className="draft-status-pill">{getStatusName(claim.currentStatus)}</span>
        </div>
      )}

      {/* ---------------------------- Reject modal ---------------------------- */}
      {rejectModal && (
        <Modal open={rejectModal} onClose={handleCloseModal}>
          <div className="custom-modal-content">
            {isContinueClicked ? (
              <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
                Confirm carefully before rejecting the claim
              </h2>
            ) : (
              <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
                Reject claim
              </h2>
            )}
            {isContinueClicked ? (
              <p>*Please note once rejected cannot be changed</p>
            ) : (
              ""
            )}
            <FormControl fullWidth variant="outlined" required>
              <InputLabel>Reason for rejection</InputLabel>
              <Select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                label="Reason for rejection"
                disabled={isContinueClicked}
                error={!reason && isContinueClicked}
              >
                {reasonList.map((item, index) => (
                  <MenuItem key={index} value={item.reason}>
                    {item.reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              variant="outlined"
              fullWidth
              value={remarks}
              required
              onChange={(e) => setRemarks(e.target.value)}
              disabled={isContinueClicked}
              style={{ marginTop: "15px" }}
              InputProps={{
                style: { height: "400px", overflow: "auto", padding: "10px" },
                inputComponent: () => (
                  <div style={{ height: "100%", overflow: "auto" }}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(newState) =>
                        handleEditorChange(newState, setRemarks)
                      }
                      stripPastedStyles={false}
                      placeholder="Remarks (to be shared with user)"
                    />
                  </div>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={(e) => {
                if (isContinueClicked) {
                  rejectClaim(id, walletId, reason, remarks);
                  setEditorState(EditorState.createEmpty());
                } else {
                  handleContinue();
                }
              }}
              className="full-width-button"
              disabled={!isFormFilled}
              style={{
                backgroundColor: isFormFilled ? "#f57c00" : "#e0e0e0",
                color: "#fff",
                marginTop: "20px",
              }}
            >
              {isContinueClicked ? "Reject the claim" : "Continue"}
            </Button>
            <button style={closeButtonStyle} onClick={handleCloseModal}>
              &times;
            </button>
          </div>
        </Modal>
      )}

      {/* --------------------------- Processed modal -------------------------- */}
      {processedModal && (
        <Modal open={processedModal} onClose={handleCloseModal}>
          <div className="custom-modal-content">
            {isContinueClicked ? (
              <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
                Check carefully before processing the claim
              </h2>
            ) : (
              <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
                Process claim
              </h2>
            )}
            {isContinueClicked ? (
              <p>
                *Please note once processed, the transaction ID cannot be
                changed.
              </p>
            ) : (
              ""
            )}
            <TextField
              required
              label="Transaction ID"
              variant="outlined"
              fullWidth
              value={processClaim}
              onChange={(e) => {
                if (e.target.value.length <= 50) {
                  setProcessClaim(e.target.value);
                }
              }}
              disabled={isContinueClicked}
              style={{ marginTop: "15px" }}
            />

            <TextField
              variant="outlined"
              fullWidth
              value={processRemarks}
              onChange={(e) => setProcessRemarks(e.target.value)}
              disabled={isContinueClicked}
              style={{ marginTop: "15px" }}
              InputProps={{
                style: { height: "400px", overflow: "auto", padding: "10px" },
                inputComponent: () => (
                  <div style={{ height: "100%", overflow: "auto" }}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(newState) =>
                        handleEditorChange(newState, setProcessRemarks)
                      }
                      stripPastedStyles={false}
                      placeholder="Remarks (to be shared with user)"
                    />
                  </div>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                if (isContinueClicked) {
                  processedClaim(id, walletId, processClaim, processRemarks);
                  setEditorState(EditorState.createEmpty());
                } else {
                  handleContinue();
                }
              }}
              className="full-width-button"
              disabled={!isFormFilled}
              style={{
                backgroundColor: isFormFilled ? "#f57c00" : "#e0e0e0",
                color: "#fff",
                marginTop: "20px",
              }}
            >
              {isContinueClicked ? "Save" : "Continue"}
            </Button>
            <button style={closeButtonStyle} onClick={handleCloseModal}>
              &times;
            </button>
          </div>
        </Modal>
      )}

      {/* ---------------------------- On hold modal --------------------------- */}
      {onHoldModal && (
        <Modal open={onHoldModal} onClose={handleCloseModal}>
          <div className="custom-modal-content scrollable-modal-full-height">
            {!showHoldConfirmation && (
              <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
                Hold claim
              </h2>
            )}
            {showHoldConfirmation && (
              <h4>Check carefully before changing the status to on hold</h4>
            )}
            {showHoldConfirmation && (
              <p style={{ fontSize: "12px" }}>
                *If the "Checkmark to Activate Upload Documents" option is
                selected, the user will be allowed to submit supporting
                documents one more time. Once the documents are successfully
                resubmitted, the claim status will automatically update to
                "Resubmitted.
              </p>
            )}

            {(clickedClaimTicketEvents ?? []).map((e, index) =>
              (e?.id ?? 0) == 6 ? (
                <div key={index} style={{ marginTop: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Previous on hold remarks (shared with user on{" "}
                    {moment
                      .utc(e?.createdAt)
                      .local()
                      .format("DD/MM/YYYY [at] hh:mmA")}
                    )
                  </label>
                  <div
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "10px",
                      paddingBottom: "0px",
                      minHeight: "56px",
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                      lineHeight: "1.5",
                      color: "#333",
                      overflowY: "auto",
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {remarkHasText(e?.comment) ? (
                      /<[a-z][\s\S]*>/i.test(e.comment) ? (
                        <div
                          style={{ width: "100%" }}
                          dangerouslySetInnerHTML={{ __html: e.comment }}
                        />
                      ) : (
                        <p style={{ margin: 0, width: "100%" }}>{e.comment}</p>
                      )
                    ) : (
                      <p style={{ margin: 0, width: "100%" }}>No remark</p>
                    )}
                  </div>
                </div>
              ) : null,
            )}

            <TextField
              variant="outlined"
              fullWidth
              required
              disabled={showHoldConfirmation}
              value={onHoldRemarks}
              onFocus={(e) => {
                if (!onHoldRemarks) {
                  setOnHoldRemarks("");
                }
              }}
              onChange={(e) => setOnHoldRemarks(e.target.value)}
              style={{ marginTop: "15px" }}
              InputProps={{
                style: { height: "400px", overflow: "auto", padding: "10px" },
                inputComponent: () => (
                  <div style={{ height: "100%", overflow: "auto" }}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(newState) =>
                        handleEditorChange(newState, setOnHoldRemarks)
                      }
                      stripPastedStyles={false}
                      placeholder="Remarks (to be shared with user)"
                    />
                  </div>
                ),
              }}
            />

            <div
              style={{
                fontSize: "12px",
                fontWeight: 400,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={onHoldUploadDocumentsAgainCheckbox ?? false}
                    color="secondary"
                    disabled={showHoldConfirmation}
                    onChange={(e) =>
                      setOnHoldUploadDocumentsAgainCheckbox(e.target.checked)
                    }
                  />
                }
                label="Activate upload documents button for this claim."
              />
            </div>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                if (showHoldConfirmation) {
                  onHoldClaim(
                    id,
                    walletId,
                    onHoldRemarks,
                    onHoldUploadDocumentsAgainCheckbox,
                  );
                  setEditorState(EditorState.createEmpty());
                } else {
                  setShowHoldConfirmation(true);
                }
              }}
              className="full-width-button"
              disabled={!isFormFilled || isClicked}
              style={{
                backgroundColor:
                  isFormFilled && !isClicked ? "#f57c00" : "#e0e0e0",
                color: "#fff",
                marginTop: "20px",
              }}
            >
              {showHoldConfirmation ? "Save" : "Continue"}
            </Button>
            <button style={closeButtonStyle} onClick={handleCloseModal}>
              &times;
            </button>
          </div>
        </Modal>
      )}

      {/* ----------------------- Provisional Approved modal ------------------- */}
      {onProvisionalModal && (
        <Modal open={onProvisionalModal} onClose={handleCloseModal}>
          <div className="custom-modal-content scrollable-modal-full-height">
            <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
              Provisional Approved claim
            </h2>

            {(clickedClaimTicketEvents ?? []).map((e, index) =>
              (e?.id ?? 0) === 7 ? (
                <div key={index} style={{ marginTop: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Previous on provisional approved remarks (shared with user
                    on{" "}
                    {moment
                      .utc(e?.createdAt)
                      .local()
                      .format("DD/MM/YYYY [at] hh:mmA")}
                    )
                  </label>
                  <div
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "10px",
                      paddingBottom: "0px",
                      minHeight: "56px",
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                      lineHeight: "1.5",
                      color: "#333",
                      overflowY: "auto",
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {remarkHasText(e?.comment) ? (
                      /<[a-z][\s\S]*>/i.test(e.comment) ? (
                        <div
                          style={{ width: "100%" }}
                          dangerouslySetInnerHTML={{ __html: e.comment }}
                        />
                      ) : (
                        <p style={{ margin: 0, width: "100%" }}>{e.comment}</p>
                      )
                    ) : (
                      <p style={{ margin: 0, width: "100%" }}>No remark</p>
                    )}
                  </div>
                </div>
              ) : null,
            )}

            <TextField
              variant="outlined"
              fullWidth
              required
              value={onProvisonalApprovedRemark}
              onChange={(e) => setOnProvisonalApprovedRemark(e.target.value)}
              style={{ marginTop: "15px" }}
              InputProps={{
                style: { height: "400px", overflow: "auto", padding: "10px" },
                inputComponent: () => (
                  <div style={{ height: "100%", overflow: "auto" }}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(newState) =>
                        handleEditorChange(
                          newState,
                          setOnProvisonalApprovedRemark,
                        )
                      }
                      stripPastedStyles={false}
                      placeholder="Remarks (to be shared with user)"
                    />
                  </div>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                handleProvisionedApprovedUpdate(
                  id,
                  walletId,
                  onProvisonalApprovedRemark,
                );
                setEditorState(EditorState.createEmpty());
              }}
              className="full-width-button"
              disabled={!isFormFilled || isClicked}
              style={{
                backgroundColor:
                  isFormFilled && !isClicked ? "#f57c00" : "#e0e0e0",
                color: "#fff",
                marginTop: "20px",
              }}
            >
              Save
            </Button>
            <button style={closeButtonStyle} onClick={handleCloseModal}>
              &times;
            </button>
          </div>
        </Modal>
      )}

      {/* --------------------------- Resubmitted modal ------------------------ */}
      {onResubmittedModal && (
        <Modal open={onResubmittedModal} onClose={handleCloseModal}>
          <div className="custom-modal-content scrollable-modal-full-height">
            <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
              Resubmitted claim
            </h2>

            {(clickedClaimTicketEvents ?? []).map((e, index) =>
              (e?.id ?? 0) === 8 ? (
                <div key={index} style={{ marginTop: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Previous on resubmitted remarks (shared with user on{" "}
                    {moment
                      .utc(e?.createdAt)
                      .local()
                      .format("DD/MM/YYYY [at] hh:mmA")}
                    )
                  </label>
                  <div
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "10px",
                      paddingBottom: "0px",
                      minHeight: "56px",
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                      lineHeight: "1.5",
                      color: "#333",
                      overflowY: "auto",
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {remarkHasText(e?.comment) ? (
                      /<[a-z][\s\S]*>/i.test(e.comment) ? (
                        <div
                          style={{ width: "100%" }}
                          dangerouslySetInnerHTML={{ __html: e.comment }}
                        />
                      ) : (
                        <p style={{ margin: 0, width: "100%" }}>{e.comment}</p>
                      )
                    ) : (
                      <p style={{ margin: 0, width: "100%" }}>No remark</p>
                    )}
                  </div>
                </div>
              ) : null,
            )}

            <TextField
              variant="outlined"
              fullWidth
              required
              value={onResubmittedRemark}
              onChange={(e) => setOnResubmittedRemark(e.target.value)}
              style={{ marginTop: "15px" }}
              InputProps={{
                style: { height: "400px", overflow: "auto", padding: "10px" },
                inputComponent: () => (
                  <div style={{ height: "100%", overflow: "auto" }}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(newState) =>
                        handleEditorChange(newState, setOnResubmittedRemark)
                      }
                      stripPastedStyles={false}
                      placeholder="Remarks (to be shared with user)"
                    />
                  </div>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                handleResubmittedUpdate(id, walletId, onResubmittedRemark);
                setEditorState(EditorState.createEmpty());
              }}
              className="full-width-button"
              disabled={!isFormFilled || isClicked}
              style={{
                backgroundColor:
                  isFormFilled && !isClicked ? "#f57c00" : "#e0e0e0",
                color: "#fff",
                marginTop: "20px",
              }}
            >
              Save
            </Button>
            <button style={closeButtonStyle} onClick={handleCloseModal}>
              &times;
            </button>
          </div>
        </Modal>
      )}

      {/* ---------------------------- Approve modal --------------------------- */}
      {approvedModal && (
        <Modal open={approvedModal} onClose={handleCloseModal}>
          {fetchingAvaClaimAmt ? (
            <div>
              <CircularProgress />
            </div>
          ) : (
            <div className="custom-modal-content">
              {isContinueClicked ? (
                <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
                  Check carefully before approving the claim
                </h2>
              ) : (
                <h2 style={{ marginTop: "10px", marginBottom: "25px" }}>
                  Approve claim
                </h2>
              )}
              {isContinueClicked ? (
                <p>*Please note once amount is deducted it cannot be undone.</p>
              ) : (
                ""
              )}
              <TextField
                required
                label="Invoice amount"
                variant="outlined"
                fullWidth
                value={selectedClaimRow?.claimAmount ?? ""}
                onChange={(e) => {}}
                disabled={true}
                type="number"
                onInput={(e) => {
                  e.target.value = Math.max(0, parseInt(e.target.value))
                    .toString()
                    .slice(0, 8);
                }}
                style={{ marginTop: "15px" }}
              />
              <div
                style={{
                  marginTop: "15px",
                  display: "flex",
                  gap: "16px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <TextField
                    required
                    label="Amount blocked"
                    variant="outlined"
                    value={selectedClaimRow?.eligibleClaimAmount ?? ""}
                    onChange={(e) => {}}
                    disabled={true}
                    type="number"
                    onInput={(e) => {
                      e.target.value = Math.max(0, parseInt(e.target.value))
                        .toString()
                        .slice(0, 8);
                    }}
                    fullWidth
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <TextField
                    required
                    label="Current available claim amount"
                    variant="outlined"
                    value={
                      currAvailableClaimAmt === null
                        ? ""
                        : currAvailableClaimAmt
                    }
                    onChange={(e) => setApproveClaim(e.target.value)}
                    disabled={true}
                    type="number"
                    onInput={(e) => {
                      e.target.value = Math.max(0, parseInt(e.target.value))
                        .toString()
                        .slice(0, 8);
                    }}
                    fullWidth
                  />
                </div>
              </div>

              {/* ----- */}
              <TextField
                required
                label="Approved claim amount"
                variant="outlined"
                fullWidth
                value={approveClaim}
                onChange={(e) => {
                  const value = e.target.value;

                  const regex = /^(?:\d+\.?\d{0,2}|\.\d{0,2})?$/;
                  if (!regex.test(value)) return;
                  const num = parseFloat(value);
                  const maxAllowed = currAvailableClaimAmt ?? 0;
                  if (value === "" || isNaN(num) || num <= maxAllowed) {
                    setApproveClaim(value);
                  }
                }}
                onBlur={() => {
                  if (!approveClaim) return;

                  let num = parseFloat(approveClaim);
                  if (isNaN(num)) num = 0;

                  const maxAllowed = currAvailableClaimAmt ?? 0;
                  num = Math.min(Math.max(num, 0), maxAllowed);
                  setApproveClaim(num.toFixed(2));
                }}
                disabled={isContinueClicked || currAvailableClaimAmt === null}
                inputProps={{
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                }}
                style={{ marginTop: "15px" }}
              />

              <TextField
                variant="outlined"
                fullWidth
                value={approveRemarks}
                onChange={(e) => setApproveRemarks(e.target.value)}
                disabled={isContinueClicked}
                style={{ marginTop: "15px" }}
                InputProps={{
                  style: { height: "200px", overflow: "auto", padding: "10px" },
                  inputComponent: () => (
                    <div style={{ height: "100%", overflow: "auto" }}>
                      <Editor
                        editorState={editorState}
                        onEditorStateChange={(newState) =>
                          handleEditorChange(
                            newState,
                            setApproveRemarks,
                            "approveRemarks",
                          )
                        }
                        stripPastedStyles={false}
                        placeholder="Remarks (if any for internal purposes)"
                      />
                    </div>
                  ),
                }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  if (isContinueClicked) {
                    deductMoney(id, walletId, approveClaim, approveRemarks);
                    setEditorState(EditorState.createEmpty());
                  } else {
                    handleContinue();
                  }
                }}
                className="full-width-button"
                disabled={!isFormFilled || isClicked}
                style={{
                  backgroundColor:
                    isFormFilled && !isClicked ? "#f57c00" : "#e0e0e0",
                  color: "#fff",
                  marginTop: "20px",
                }}
              >
                {isContinueClicked ? "Deduct amount from wallet" : "Continue"}
              </Button>
              <button style={closeButtonStyle} onClick={handleCloseModal}>
                &times;
              </button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

export default ClaimStatusCell;
